"use client";

import { useRef, useState } from "react";
import { MapPin, X } from "lucide-react";
import { MapPicker } from "@/design/MapPicker";
import { KeywordSuggestions } from "@/design/KeywordSuggestions";
import {
  DEFAULT_RADIUS_M,
  PRICE_SLIDER_STOPS,
  priceFromStop,
  priceSliderLabel,
  RATING_SLIDER_VALUES,
  ratingFromStop,
  ratingSliderLabel,
  stopForPrice,
  stopForRating,
  type Suggestion,
} from "@/modules/discovery/surfaces";
import {
  SERVICE_INPUT_CLASS,
  FilterButton,
  FilterGroup,
  FilterPanel,
  OptionRow,
  PopoverAnchor,
  SearchBar,
  SheetDim,
  whereTriggerClass,
} from "@/design/SearchChrome";

/**
 * SearchScreen filter controls (docs/ux/screens/SearchScreen.md), rebuilt on
 * design system v2.
 *
 * The URL query string is the single source of truth: every change rewrites
 * `/search?…` and the server re-runs `discovery.searchBusinesses`. Pure props in
 * (no module imports); the option lists and current values are computed by the
 * server page. Proximity (H11) is browser geolocation with a map-pin fallback;
 * denying location leaves the other filters fully working.
 *
 * ## One search, and only one
 *
 * There is exactly **one** button on this screen that runs a search, and it says
 * Search. An earlier version had a second one: filters were staged in the panel
 * and committed by a "Show results" button of their own. That was not just two
 * buttons doing the same job, it was a trap. Choose a price, close the panel,
 * press Search, and the price was silently discarded, because Search knew only
 * about the keyword and the staged filter had never been applied.
 *
 * So nothing is staged. Every control here commits the moment it is used:
 *
 * - a **filter slider** applies itself as it is let go and re-runs the search
 * - a **category chip** applies itself
 * - **Search** applies whatever is typed in the keyword box
 *
 * And every one of them commits the *whole* screen, not just its own value.
 * `pushWith` reads the keyword box live, so choosing a filter after typing
 * "dentist" searches for a cheap dentist rather than throwing the word away.
 * That is the property the two-button version was missing in both directions.
 *
 * The panel's footer therefore has no apply. "Done" only dismisses the sheet,
 * because by the time you press it the filters have already been applied.
 *
 * ## Location is on the surface, not in the panel
 *
 * Proximity sits next to the Search button rather than behind Filters. In a
 * country the size of Lebanon, "near me" is not a refinement someone applies
 * after browsing, it is part of the question they are asking, and burying it
 * two taps deep meant most people searched the whole country by accident. It
 * lives in exactly one place: the panel holds price and rating only, so there
 * is no second distance control to disagree with this one.
 *
 * ## Navigation arrives as a prop (R22 T12)
 *
 * Everything above is true of both frontends, and none of it is about routing,
 * so the one line that *was* about routing became a `push` prop. Each host
 * hands in its own router in about ten lines, and the five hundred that decide
 * what a filter does are shared rather than copied. Two copies of "which
 * control commits the whole screen" is exactly the trap this file's history is
 * a record of.
 */

interface CategoryOption {
  id: string;
  name: string;
}
export interface SearchControlsState {
  categoryId: string | null;
  keyword: string | null;
  maxPriceCents: number | null;
  minRating: number | null;
  point: { lat: number; lng: number } | null;
  /** Null with a point: nearest first, nothing filtered out (H56). */
  radiusM: number | null;
}

const RADIUS_OPTIONS = [
  { value: 1000, label: "1 km" },
  { value: 5000, label: "5 km" },
  { value: 10000, label: "10 km" },
  { value: 25000, label: "25 km" },
];

export function SearchControls({
  categories,
  current,
  push,
  suggest,
}: {
  categories: CategoryOption[];
  current: SearchControlsState;
  /** Navigate to a `/search?…` URL, or to a clinic. The host's router, injected. */
  push: (url: string) => void;
  /** What to suggest for the typed text (H56). The host's API, injected. */
  suggest: (typed: string) => Promise<Suggestion[]>;
}) {
  const keywordRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  // Phone only: the bar starts folded. From `sm` up CSS keeps it open and this
  // never comes into play.
  const [expanded, setExpanded] = useState(false);

  /**
   * Rebuild the query from everything currently on screen, plus a patch (null
   * clears a key). The keyword comes from the live input rather than from the
   * applied URL, so no control ever discards what someone has typed.
   */
  function pushWith(patch: Record<string, string | null>) {
    const sp = new URLSearchParams();
    if (current.categoryId) sp.set("category", current.categoryId);

    const typed = keywordRef.current?.value ?? current.keyword ?? "";
    if (typed.trim() !== "") sp.set("q", typed.trim());

    if (current.maxPriceCents !== null) sp.set("maxPrice", String(current.maxPriceCents));
    if (current.minRating !== null) sp.set("minRating", String(current.minRating));
    if (current.point) {
      sp.set("lat", String(current.point.lat));
      sp.set("lng", String(current.point.lng));
      if (current.radiusM !== null) sp.set("radius", String(current.radiusM));
    }

    for (const [key, value] of Object.entries(patch)) {
      if (value === null) sp.delete(key);
      else sp.set(key, value);
    }

    const qs = sp.toString();
    push(qs ? `/search?${qs}` : "/search");
  }

  const activeCategory = categories.find((c) => c.id === current.categoryId);
  // The badge counts only what the button hides. Keyword and category are on
  // the surface, so counting them would make the number contradict the screen.

  const hiddenCount =
    (current.maxPriceCents !== null ? 1 : 0) + (current.minRating !== null ? 1 : 0);

  const hasFilters =
    hiddenCount > 0 || current.categoryId !== null || current.keyword !== null;

  return (
    <div className="mb-8 rounded-2xl border border-line bg-surface p-4 shadow-[var(--shadow-card)] sm:p-5">
      {/* Chrome from SearchChrome, behaviour from here. The demo renders the
          same shell against fixtures, which is what stops the two drifting. */}
      <SearchBar
        expanded={expanded}
        onExpand={() => setExpanded(true)}
        onSubmit={() => {
          setExpanded(false);
          pushWith({});
        }}
        foldedTitle={current.keyword ?? "Start your search"}
        foldedHint={summaryLine(current, activeCategory?.name)}
        care={
          <KeywordSuggestions
            inputRef={keywordRef}
            suggest={suggest}
            onPick={(suggestion) => {
              setExpanded(false);
              if (suggestion.kind === "clinic") {
                push(`/businesses/${encodeURIComponent(suggestion.id)}`);
              } else {
                // A service is a category, the wider search: the chip goes
                // on and the typed fragment comes off, since it was only ever
                // a way of finding the category's name.
                if (keywordRef.current) keywordRef.current.value = "";
                pushWith({ category: suggestion.id, q: null });
              }
            }}
          >
            <input
              ref={keywordRef}
              key={current.keyword ?? ""}
              id="q"
              name="q"
              type="search"
              autoComplete="off"
              defaultValue={current.keyword ?? ""}
              placeholder="Treatment, clinic or specialist"
              className={SERVICE_INPUT_CLASS}
            />
          </KeywordSuggestions>
        }
        where={
          <LocationControl
            point={current.point}
            radiusM={current.radiusM}
            onApply={pushWith}
          />
        }
      />

      {/* One row of things to narrow by, all at chip scale: the categories,
          then price and rating as their own chips. No "Filters" button, because
          a panel that hides two options costs a tap and hides the answer. The
          chips wrap rather than scroll sideways. */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {categories.length > 0 && (
          <>
            <Chip
              active={current.categoryId === null}
              onClick={() => pushWith({ category: null })}
            >
              All
            </Chip>
            {categories.map((c) => (
              <Chip
                key={c.id}
                active={current.categoryId === c.id}
                onClick={() => pushWith({ category: c.id })}
              >
                {c.name}
              </Chip>
            ))}
          </>
        )}

        {/* Always at the end of the row, whatever else is in it. */}
        <PopoverAnchor
          open={open}
          onClose={() => setOpen(false)}
          className="relative ms-auto"
        >
          <FilterButton
            count={hiddenCount}
            expanded={open}
            onClick={() => setOpen((v) => !v)}
          />
          {open && (
            <FilterPanel
              anySet={hiddenCount > 0}
              sliders={[
                {
                  label: "Maximum price",
                  stop: stopForPrice(current.maxPriceCents),
                  stops: PRICE_SLIDER_STOPS,
                  describe: (stop) => priceSliderLabel(priceFromStop(stop)),
                  onCommit: (stop) => {
                    const cents = priceFromStop(stop);
                    pushWith({ maxPrice: cents === null ? null : String(cents) });
                  },
                },
                {
                  label: "Minimum rating",
                  stop: stopForRating(current.minRating),
                  stops: RATING_SLIDER_VALUES.length,
                  describe: (stop) => ratingSliderLabel(ratingFromStop(stop)),
                  onCommit: (stop) => {
                    const rating = ratingFromStop(stop);
                    pushWith({ minRating: rating === null ? null : String(rating) });
                  },
                },
              ]}
              onClear={() => pushWith({ maxPrice: null, minRating: null })}
              onClose={() => setOpen(false)}
            />
          )}
        </PopoverAnchor>
      </div>

      {/* What's narrowing the results, and how to undo it. Everything applied
          appears here, including the filters the panel hides, so the panel is
          never the only place to see what is going on. */}
      {hasFilters && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4">
          {current.keyword && (
            <ActiveChip
              label={`“${current.keyword}”`}
              onClear={() => {
                if (keywordRef.current) keywordRef.current.value = "";
                pushWith({ q: null });
              }}
            />
          )}
          {activeCategory && (
            <ActiveChip
              label={activeCategory.name}
              onClear={() => pushWith({ category: null })}
            />
          )}
          {current.maxPriceCents !== null && (
            <ActiveChip
              label={priceSliderLabel(current.maxPriceCents)}
              onClear={() => pushWith({ maxPrice: null })}
            />
          )}
          {current.minRating !== null && (
            <ActiveChip
              label={ratingSliderLabel(current.minRating)}
              onClear={() => pushWith({ minRating: null })}
            />
          )}
          {current.point && (
            <ActiveChip
              label={
                current.radiusM === null
                  ? "Nearest to you"
                  : `Within ${radiusLabel(current.radiusM)} of you`
              }
              onClear={() => pushWith({ lat: null, lng: null, radius: null })}
            />
          )}
          <button
            type="button"
            onClick={() => {
              if (keywordRef.current) keywordRef.current.value = "";
              push("/search");
            }}
            className="ms-auto text-sm font-semibold text-muted transition-colors hover:text-body"
          >
            Clear all
          </button>
        </div>
      )}

      {current.point && (
        <p className="mt-3 text-sm text-muted">Sorted by distance from your location.</p>
      )}
    </div>
  );
}

/** What the folded trigger says underneath, so it summarises rather than nags. */
function summaryLine(current: SearchControlsState, categoryName?: string): string {
  const parts = [
    categoryName,
    current.point
      ? current.radiusM === null
        ? "nearest first"
        : `within ${radiusLabel(current.radiusM)}`
      : "anywhere",
  ].filter(Boolean);
  return parts.join(" · ");
}

function radiusLabel(radiusM: number): string {
  return RADIUS_OPTIONS.find((o) => o.value === radiusM)?.label ?? `${radiusM} m`;
}

/**
 * Where to search, next to the box you search in.
 *
 * Unset it is a single tap: "Near me" asks the browser and searches. Set, it
 * becomes a pill showing the radius, and opens a small menu to change it, move
 * the point on a map, or forget it.
 *
 * Denying location is not a dead end. It opens the map straight away, so the
 * fallback (H11) is one tap from the refusal rather than something to go and
 * find, and every other filter keeps working either way.
 */
function LocationControl({
  point,
  radiusM,
  onApply,
}: {
  point: { lat: number; lng: number } | null;
  radiusM: number | null;
  onApply: (patch: Record<string, string | null>) => void;
}) {
  // A shared link with a point and no radius lists everything nearest first
  // (H56). The web's menu always states a radius, so picking a point from here
  // starts at the default one rather than inheriting the absence.
  const radius = radiusM ?? DEFAULT_RADIUS_M;
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);

  function locate() {
    setNote(null);
    if (!navigator.geolocation) {
      setNote("Location isn't available on this device. Pick a point on the map.");
      setShowMap(true);
      setOpen(true);
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocating(false);
        setOpen(false);
        onApply({
          lat: coords.latitude.toFixed(6),
          lng: coords.longitude.toFixed(6),
          radius: String(radius),
        });
      },
      () => {
        setLocating(false);
        setNote("We couldn't get your location. Pick a point on the map instead.");
        setShowMap(true);
        setOpen(true);
      },
    );
  }

  return (
    <PopoverAnchor open={open} onClose={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => (point ? setOpen((v) => !v) : locate())}
        disabled={locating}
        aria-expanded={point ? open : undefined}
        aria-haspopup={point ? "dialog" : undefined}
        className={whereTriggerClass(point !== null)}
      >
        <MapPin className="size-4 shrink-0" aria-hidden strokeWidth={1.8} />
        <span className="truncate">
          {locating
            ? "Locating…"
            : point
              ? radiusM === null
                ? "Nearest first"
                : `Within ${radiusLabel(radiusM)}`
              : "Near me"}
        </span>
      </button>

      {open && (
        <>
          <SheetDim />
          <div
            role="dialog"
            aria-label="Where to search"
            className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-[var(--shadow-lift)] sm:absolute sm:inset-x-auto sm:bottom-auto sm:start-0 sm:top-11 sm:max-h-[min(30rem,70vh)] sm:w-72 sm:rounded-2xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-ink">Where to search</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close location options"
                className="-me-1 inline-flex size-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-page hover:text-ink"
              >
                <X className="size-4.5" aria-hidden />
              </button>
            </div>

            {note && <p className="mb-3 text-sm text-muted">{note}</p>}

            <FilterGroup label="Distance">
              {RADIUS_OPTIONS.map((o) => (
                <OptionRow
                  key={o.value}
                  label={`Within ${o.label}`}
                  selected={radiusM === o.value}
                  onClick={() => onApply({ radius: String(o.value) })}
                />
              ))}
            </FilterGroup>

            {showMap ? (
              <div className="mb-4">
                <MapPicker
                  label="Search location"
                  emitInputs={false}
                  onChange={(p) => {
                    setOpen(false);
                    onApply({
                      lat: p.lat.toFixed(6),
                      lng: p.lng.toFixed(6),
                      radius: String(radius),
                    });
                  }}
                />
                <p className="mt-2 text-xs text-muted">
                  Tap the map to search around that point.
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowMap(true)}
                className="mb-4 block text-sm font-semibold text-accent hover:underline"
              >
                Pick a different point on the map
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onApply({ lat: null, lng: null, radius: null });
              }}
              className="border-t border-line pt-4 text-sm font-semibold text-muted transition-colors hover:text-body"
            >
              Search everywhere instead
            </button>
          </div>
        </>
      )}
    </PopoverAnchor>
  );
}

/** A one-tap filter pill. */
function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`h-9 shrink-0 whitespace-nowrap rounded-lg px-4 text-sm font-semibold transition duration-150 ${
        active
          ? "bg-primary text-on-primary"
          : "border border-line bg-surface text-body hover:border-accent/50 hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

/** An applied filter, echoed back with a one-click undo. */
function ActiveChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-accent/12 py-1 pe-1.5 ps-3 text-xs font-semibold text-accent">
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Remove filter ${label}`}
        className="rounded-md p-0.5 transition-colors hover:bg-accent/20"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </span>
  );
}
